import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { setSceneSpecifierOverride, setSceneMeta } from '../../../../store/visualizerSlice';
import StepNavigation from '../../StepNavigation/StepNavigation';
import styles from './BathType.module.css';

const SPEC_PREFIX = 'Holland_Homes/Images/2026_Redo/';

// Master list of bath scenes. Keys = scene number from the source table.
// hasFloor / hasWall drive visibility of the downstream Shower Floor / Shower Wall steps.
//
// NOTE (2026-08-06 client feedback):
//   - Scene 8 (Bath_Fiberglass_Tub_Shower_Combo) is the actual fiberglass
//     tub/shower COMBO — earlier the image was mislabeled as "Fiberglass Shower".
//   - Scene 10 (Americast Short Tile Surround) is a duplicate of scene 6 and
//     has been removed from the option lists below.
export const BATH_SCENES = {
    1:  { specifier: 'Primary_Bathroom',                                              label: 'Full Tile Custom Shower',            hasFloor: true,  hasWall: true  },
    2:  { specifier: 'Primary_Bathroom_Fiberglass_Base',                              label: 'Fiberglass Base, Tile Walls',        hasFloor: false, hasWall: true  },
    3:  { specifier: 'Primary_Bathroom_Fiberglass_Tub_Shower',                        label: 'Fiberglass Tub Shower Combo',        hasFloor: false, hasWall: false },
    4:  { specifier: 'Primary_Bathroom_Fiberglass_Shower',                            label: 'Fiberglass Shower',                  hasFloor: false, hasWall: false },
    5:  { specifier: 'Primary_Bathroom_Fiberglass_Tub_Tile',                          label: 'Fiberglass Tub, Tile Surround',      hasFloor: false, hasWall: true  },
    6:  { specifier: 'Bath_Fiberglass_Tub_Shower_Americast_Tub_Tile_Surround',        label: 'Americast Tub, Tile Surround',       hasFloor: false, hasWall: true  },
    7:  { specifier: 'Bath_Fiberglass_Tub_Shower_Ceramic_Tile',                       label: 'Ceramic Tile Shower',                hasFloor: true,  hasWall: true  },
    8:  { specifier: 'Bath_Fiberglass_Tub_Shower_Combo',                              label: 'Fiberglass Tub/Shower Combo',        hasFloor: false, hasWall: false },
    9:  { specifier: 'Bath_Fiberglass_Tub_Shower_Tile_Fiberglass_Base',               label: 'Tile Shower, Fiberglass Base',       hasFloor: false, hasWall: true  },
    10: { specifier: 'Bath_Fiberglass_Tub_Shower_Americast_Tub_Tile_Surround_Short',  label: 'Americast Tub, Short Tile Surround', hasFloor: false, hasWall: true  },
    11: { specifier: 'Primary_Bathroom_Semi_Frameless',                               label: 'Primary Bathroom Semi Frameless',    hasFloor: true,  hasWall: true  }
};

// Community-code → scene-number list, per room.
//
// Per 2026-08-06 client feedback:
//   - Standard bath (CC/EV/FF/FR/NG/SC Primary+Guest, PR/AF Guest) leads with
//     scene 8 (fiberglass tub/shower combo). Scene 10 was a duplicate of 6
//     and is removed.
//   - PR/AF Primary bath leads with scene 11 (Semi Frameless — new standard),
//     then 1 (Full Tile Custom Shower).
export function getSceneNumbersFor(communityCode, room) {
    const code = String(communityCode || '').toUpperCase();
    const isPrimary = /primary/i.test(room || '');
    const isGuest = /guest/i.test(room || '');
    const isPRAF = code === 'PR' || code === 'AF';

    const standardBath = [8, 9, 7, 6];
    const prafPrimary  = [11, 1];

    if (isGuest) return standardBath;
    if (isPrimary) return isPRAF ? prafPrimary : standardBath;
    // Legacy "Bathroom" area (pre-migration) — fall back to primary rules.
    return isPRAF ? prafPrimary : standardBath;
}

export function buildBathOptions(communityCode, room) {
    const roomSlug = String(room || 'bath').replace(/\s+/g, '-').toLowerCase();
    return getSceneNumbersFor(communityCode, room).map(n => {
        const s = BATH_SCENES[n];
        // Tub configurations normally hide the downstream Shower Floor / Wall
        // steps — EXCEPT tub-with-tile-surround options (Fiberglass Tub /
        // Americast Tub / Americast Short), which do expose the tile-wall
        // step per master data. Applies to all floorplans (2026-08-14).
        const isTub = /\btub\b/i.test(s.label);
        const hasTileSurround = /tile\s*surround/i.test(s.label);
        const stripTile = isTub && !hasTileSurround;
        return {
            key: `${roomSlug}-${n}`,
            sceneNumber: n,
            label: s.label,
            specifier: SPEC_PREFIX + s.specifier,
            hasFloor: stripTile ? false : s.hasFloor,
            hasWall: stripTile ? false : s.hasWall
        };
    });
}

// Public helper — VisualizerPage / slice use it to gate downstream steps.
export function getBathCapabilities(sceneKey, communityCode, room) {
    const options = buildBathOptions(communityCode, room);
    const opt = options.find(o => o.key === sceneKey);
    if (!opt) return { hasFloor: false, hasWall: false };
    return { hasFloor: !!opt.hasFloor, hasWall: !!opt.hasWall };
}

function thumbUrl(specifier) {
    return `https://renderfarm2.chameleonpower.com/proxy/GetImage.ashx?spec=${encodeURIComponent(specifier)}&sel=-1&type=base&size=508`;
}

export default function BathType({
    stepIndex,
    room = 'Primary Bath',
    onViewAllApplied,
    onNext,
    onPrevious
}) {
    const dispatch = useDispatch();
    const sceneMeta = useSelector(state => state.visualizer.sceneMeta);
    const activeFloorplanId = useSelector(state => state.visualizer.activeFloorplanId);
    const floorplans = useSelector(state => state.floorplan.floorplans);

    const communityCode = useMemo(() => {
        const fp = (floorplans || []).find(f => String(f.Id || f.id) === String(activeFloorplanId));
        return String(fp?.Code || fp?.code || '').trim().toUpperCase();
    }, [floorplans, activeFloorplanId]);

    const options = useMemo(() => buildBathOptions(communityCode, room), [communityCode, room]);

    const suggestedKey = options[0]?.key;
    const selectedKey = sceneMeta?.[room]?.bathType || suggestedKey;

    // If no bath type has been chosen yet for this room, seed the default so
    // downstream Shower Floor / Shower Wall gating reflects the visually
    // active option (they would otherwise stay visible for tub-only defaults).
    const hasChosen = !!sceneMeta?.[room]?.bathType;
    useEffect(() => {
        if (hasChosen) return;
        const defaultOpt = options[0];
        if (!defaultOpt) return;
        dispatch(setSceneSpecifierOverride({ area: room, specifier: defaultOpt.specifier }));
        dispatch(setSceneMeta({
            area: room,
            meta: {
                bathType: defaultOpt.key,
                bathScene: defaultOpt.sceneNumber,
                showerHasFloor: !!defaultOpt.hasFloor,
                showerHasWall: !!defaultOpt.hasWall
            }
        }));
    }, [hasChosen, options, room, dispatch]);

    const handleSelect = (opt) => {
        dispatch(setSceneSpecifierOverride({ area: room, specifier: opt.specifier }));
        dispatch(setSceneMeta({
            area: room,
            meta: {
                bathType: opt.key,
                bathScene: opt.sceneNumber,
                showerHasFloor: !!opt.hasFloor,
                showerHasWall: !!opt.hasWall
            }
        }));
    };

    const title = /guest/i.test(room) ? 'Guest Bath Type' : 'Primary Bath Type';

    return (
        <div className={styles.wrapper}>
            <StepNavigation
                title={title}
                onNext={onNext}
                onPrevious={onPrevious}
                onViewAllApplied={onViewAllApplied}
            />

            <div className={styles.instructions}>
                Choose the {/guest/i.test(room) ? 'guest bath' : 'primary bath'} configuration.
                Additional tile steps will appear when applicable.
            </div>

            {options.length === 0 && (
                <div className={styles.instructions} style={{ color: '#a33' }}>
                    No bath configurations available for this community.
                </div>
            )}

            <div className={styles.grid}>
                {options.map(opt => (
                    <button
                        key={opt.key}
                        type="button"
                        className={`${styles.card} ${selectedKey === opt.key ? styles.cardActive : ''}`}
                        onClick={() => handleSelect(opt)}
                    >
                        <img
                            src={thumbUrl(opt.specifier)}
                            alt={opt.label}
                            className={styles.thumb}
                        />
                        <div className={styles.title}>{opt.label}</div>
                        <div className={styles.sub}>
                            {opt.hasFloor || opt.hasWall
                                ? `Tile ${opt.hasFloor ? 'floor' : ''}${opt.hasFloor && opt.hasWall ? ' & ' : ''}${opt.hasWall ? 'walls' : ''}`
                                : 'No tile customization'}
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

BathType.propTypes = {
    stepIndex: PropTypes.number.isRequired,
    room: PropTypes.string,
    onViewAllApplied: PropTypes.func,
    onNext: PropTypes.func,
    onPrevious: PropTypes.func
};
